import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/feature/PageMeta";
import PageHero from "@/components/feature/PageHero";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { faqItems, sourceLevels } from "@/mocks/site";

const workflow = [
  { step: "登记", desc: "记录最早出处、出现时间与原始表述，先当线索，不当事实。" },
  { step: "追源", desc: "顺引用一路回查，直到定位到最早材料或确认追不动为止。" },
  { step: "对照", desc: "把相关材料并排比较，重点排查「来自同一链路」的假印证。" },
  { step: "标注", desc: "给出已核实、待证实或存在矛盾的状态，并保留修订记录。" },
];

export default function FaqPage() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://balha.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "常见问题",
          item: "https://balha.cn/faq",
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <PageMeta
        title="511求证档案常见问题｜来源标注与状态说明"
        description="关于511爆料求证档案的常见问题：状态标注含义、来源独立性判断、时间基准、更正机制与线索提交方式，一次说清楚。"
        keywords="511求证档案常见问题,511爆料来源求实,状态标注,线索提交,时间线整理"
        path="/faq"
        schema={schema}
      />

      <PageHero
        eyebrow="FAQ"
        title="常见问题：先理解标注，再看材料"
        description="档案里出现的每个状态都有明确定义。看懂这些定义，你就能判断一条内容该信到什么程度。"
        crumbs={
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
        }
      />

      <section className="w-full px-4 py-10 md:px-6 md:py-14 lg:px-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
          <div className="lg:col-span-2">
            <FaqAccordion items={faqItems} defaultOpen={0} />
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                求证流程四步
              </h2>
              <ol className="mt-4 flex flex-col gap-4">
                {workflow.map((item, index) => (
                  <li key={item.step} className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-xs font-bold text-primary-700">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-heading text-sm font-semibold text-foreground-900">
                        {item.step}
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                        {item.desc}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                来源分级速查
              </h2>
              <ul className="mt-4 flex flex-col gap-2.5">
                {sourceLevels.map((item) => (
                  <li
                    key={item.level}
                    className="flex items-center justify-between gap-3 text-xs"
                  >
                    <span className="whitespace-nowrap rounded bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
                      {item.level}
                    </span>
                    <span className="text-right text-foreground-600">{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-primary-200 bg-primary-100 p-5">
              <h2 className="font-heading text-base font-semibold text-primary-800">
                还有疑问？
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-primary-800/90">
                如果问题不在上面，可以直接通过表单联系我们，或在提交线索时一并说明。
              </p>
              <Link
                to="/about#tips"
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                联系求证档案
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}